import { SvgIcon, SvgIconProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Nexo brand marks.
 *
 * Source of truth: `docs/branding/Mimerium_Logo_Nexo_{blue,green}.svg`. Those two files are
 * byte-identical apart from the fill, so the geometry lives here once and the colour is a
 * prop — shipping both files would mean maintaining the same paths twice.
 */

/** Wordmark fills, straight from the brand SVGs. */
const BRAND_BLUE = '#18233e';  // for light backgrounds
const BRAND_GREEN = '#43b69e'; // for dark backgrounds

/** Full "NEXO" wordmark, viewBox 800 × 162.79. */
const WORDMARK_PATHS = [
  'M103.25,158.51l-62.58-91.88v91.88H4.32V4.27h36.35l62.58,91.45V4.27h36.35v154.24h-36.35Z',
  'M236.68,158.51V4.27h101.14v33.5h-64.34v24.9h57.95v33.05h-57.95v29.3h64.34v33.5h-101.14Z',
  'M560.47,158.51h-42.07l-29.53-46.06-29.53,46.06h-42.09l50.68-78.89L419.45,4.27h42.09l27.32,42.52,27.32-42.52h42.07l-48.24,75.35,50.45,78.89Z',
  'M631.09,81.4c0-45.39,35.92-79.34,82.19-79.34s82.4,33.95,82.4,79.34-36.13,79.32-82.4,79.32-82.19-33.93-82.19-79.32ZM756.01,81.4c0-24.24-17.83-42.97-42.74-42.97s-42.54,18.73-42.54,42.97,18.08,42.95,42.54,42.95,42.74-18.73,42.74-42.95Z',
];

/**
 * The wordmark's own "N" glyph, reused as the icon mark. The brand set has no separate
 * monogram, and the letterform is a truer mark than an invented one. Its bounds are
 * x 4.32–139.6, y 4.27–158.51, so this viewBox keeps it optically centred.
 */
const MONOGRAM_PATH = WORDMARK_PATHS[0];

interface NexoLogoProps {
  /**
   * Which background the mark sits on: 'light' → brand blue, 'dark' → brand green.
   * Omit to follow the active theme, which is almost always what you want.
   */
  variant?: 'light' | 'dark';
  height?: number;
}

function useBrandColor(variant?: 'light' | 'dark') {
  const theme = useTheme();
  const onDark = variant ? variant === 'dark' : theme.palette.mode === 'dark';
  return onDark ? BRAND_GREEN : BRAND_BLUE;
}

/**
 * Nexo icon mark (the wordmark's "N").
 * Use for favicon-sized contexts, collapsed sidebar, etc.
 */
export function NexoIcon({ variant, ...props }: SvgIconProps & { variant?: 'light' | 'dark' }) {
  const color = useBrandColor(variant);

  return (
    <SvgIcon {...props} viewBox="0 0 144 163">
      <path d={MONOGRAM_PATH} fill={color} />
    </SvgIcon>
  );
}

/**
 * Full Nexo wordmark.
 * Height drives the size; width follows the 800:162.79 aspect ratio (~4.9:1).
 */
export function NexoLogo({ variant, height = 36 }: NexoLogoProps) {
  const color = useBrandColor(variant);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 800 162.79"
      height={height}
      role="img"
      aria-label="Nexo"
      style={{ display: 'block' }}
    >
      {WORDMARK_PATHS.map((d) => (
        <path key={d} d={d} fill={color} />
      ))}
    </svg>
  );
}
